import { DragEvent, useEffect, useRef, useState } from "react";
import {
  Box,
  Stack,
  TextField,
  Typography,
  alpha,
  useTheme,
} from "@mui/material";
import { CloudUpload, Description } from "@mui/icons-material";
import { toast } from "react-toastify";

import { CommonDialog } from "@/components/common/CommonDialog";
import { PrimaryButton, SecondaryButton } from "@/components/common";
import {
  isReactNativeWebView,
  selectFileRN,
} from "@/utils/useWebViewBridge";

import {
  PatientSupportingDocumentRecord,
  PatientSupportingDocumentTypeConfig,
} from "./types";

interface PatientSupportingDocumentDialogProps {
  open: boolean;
  onClose: () => void;
  config: PatientSupportingDocumentTypeConfig;
  existing?: PatientSupportingDocumentRecord;
  onSave: (payload: {
    file: File | null;
    notes: string;
    description: string;
  }) => Promise<boolean>;
}

/** Valida extensão/MIME contra o accept do tipo (ex.: ".pdf,.jpg,.jpeg,.png"). */
function isFileAccepted(file: File, accept: string): boolean {
  const rules = accept
    .split(",")
    .map((part) => part.trim().toLowerCase())
    .filter(Boolean);
  if (!rules.length) return true;

  const fileName = file.name.toLowerCase();
  const mime = (file.type || "").toLowerCase();

  return rules.some((rule) => {
    if (rule.startsWith(".")) {
      return fileName.endsWith(rule);
    }
    if (rule.endsWith("/*")) {
      return mime.startsWith(rule.slice(0, -1));
    }
    return mime === rule;
  });
}

/** Converte o payload do seletor nativo (data URL ou base64 puro) em File. */
async function fileFromNativeSelection(result: {
  base64: string;
  fileName: string;
  mimeType: string;
}): Promise<File> {
  const mimeType = result.mimeType || "application/octet-stream";
  const dataUrl = result.base64.startsWith("data:")
    ? result.base64
    : `data:${mimeType};base64,${result.base64}`;
  const response = await fetch(dataUrl);
  const blob = await response.blob();
  return new File([blob], result.fileName || "arquivo", {
    type: mimeType || blob.type || "application/octet-stream",
  });
}

export function PatientSupportingDocumentDialog({
  open,
  onClose,
  config,
  existing,
  onSave,
}: PatientSupportingDocumentDialogProps) {
  const theme = useTheme();
  const inputRef = useRef<HTMLInputElement>(null);
  const [notes, setNotes] = useState("");
  const [documentDescription, setDocumentDescription] = useState("");
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [saving, setSaving] = useState(false);
  // Feedback visual enquanto o arquivo está sobre a área de drop.
  const [isDragging, setIsDragging] = useState(false);

  useEffect(() => {
    if (!open) return;
    setNotes(existing?.notes ?? "");
    setDocumentDescription(existing?.description ?? "");
    setSelectedFile(null);
    setIsDragging(false);
  }, [open, existing]);

  const applySelectedFile = (file: File | null) => {
    if (!file) return;
    if (!isFileAccepted(file, config.accept)) {
      toast.error(`Formato não permitido. Use: ${config.acceptHint}`);
      return;
    }
    setSelectedFile(file);
  };

  /** Browser: input file. App (RN WebView): seletor nativo via bridge. */
  const handlePickFile = () => {
    if (isReactNativeWebView()) {
      selectFileRN(config.accept, async (result) => {
        try {
          const file = await fileFromNativeSelection(result);
          applySelectedFile(file);
        } catch (error) {
          console.error("[Documentos] Erro ao receber arquivo do app:", error);
          toast.error("Não foi possível carregar o arquivo.");
        }
      });
      return;
    }
    inputRef.current?.click();
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const ok = await onSave({
        file: selectedFile,
        notes,
        description: documentDescription,
      });
      if (ok) onClose();
    } finally {
      setSaving(false);
    }
  };

  // Necessário para o drop funcionar no browser.
  const handleDragOver = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
    if (!isDragging) setIsDragging(true);
  };

  const handleDragLeave = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
    // Evita resetar o destaque ao passar por filhos (ícone/texto) da área.
    const related = event.relatedTarget as Node | null;
    if (related && event.currentTarget.contains(related)) return;
    setIsDragging(false);
  };

  const handleDrop = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragging(false);
    // Drag-and-drop não existe no app; só no browser.
    if (isReactNativeWebView()) return;
    const file = event.dataTransfer.files?.[0] ?? null;
    applySelectedFile(file);
  };

  const accent = theme.palette[config.accentKey].main;
  const previewName = selectedFile?.name ?? existing?.fileName;

  return (
    <CommonDialog
      isOpen={open}
      handleClose={() => {
        if (!saving) onClose();
      }}
      maxWidth="sm"
      sheetTitle={existing ? "Editar documento" : "Adicionar documento"}
      sheetSubtitle={config.title}
      sheetSize="auto"
      footer={
        <Stack
          direction={{ xs: "column-reverse", sm: "row" }}
          spacing={1}
          justifyContent="flex-end"
          width="100%"
        >
          <SecondaryButton
            width="100%"
            height="40px"
            onClick={onClose}
            disabled={saving}
          >
            Cancelar
          </SecondaryButton>
          <PrimaryButton
            width="100%"
            height="40px"
            onClick={handleSave}
            disabled={saving || (!selectedFile && !existing)}
            loading={saving}
          >
            Salvar
          </PrimaryButton>
        </Stack>
      }
    >
      <Stack spacing={2} sx={{ mt: { xs: 0, md: 1 } }}>
        <Box>
          <Typography variant="body2" color="text.secondary">
            {config.description}
          </Typography>
        </Box>

        <Box
          onClick={handlePickFile}
          onDragOver={handleDragOver}
          onDragEnter={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          sx={{
            border: "1px dashed",
            borderColor: isDragging ? accent : alpha(accent, 0.45),
            borderRadius: 2,
            bgcolor: isDragging ? alpha(accent, 0.16) : alpha(accent, 0.06),
            px: 2,
            py: 2.5,
            textAlign: "center",
            cursor: "pointer",
            transition: "background-color 0.15s ease, border-color 0.15s ease",
            "&:hover": { bgcolor: alpha(accent, 0.1) },
          }}
        >
          <CloudUpload sx={{ color: accent, fontSize: 32, mb: 1 }} />
          <Typography variant="body2" fontWeight={600}>
            {isDragging
              ? "Solte o arquivo aqui"
              : selectedFile || existing
                ? "Toque para substituir o arquivo"
                : "Toque para selecionar o arquivo"}
          </Typography>
          <Typography
            variant="caption"
            color="text.secondary"
            display="block"
            mt={0.5}
          >
            {config.acceptHint}
          </Typography>
          {previewName ? (
            <Stack
              direction="row"
              spacing={0.75}
              alignItems="center"
              justifyContent="center"
              mt={1.5}
            >
              <Description sx={{ fontSize: 18, color: accent }} />
              <Typography variant="caption" fontWeight={600}>
                {previewName}
              </Typography>
            </Stack>
          ) : null}
          {/* Input só no browser — no RN o seletor é nativo via bridge. */}
          {!isReactNativeWebView() ? (
            <input
              ref={inputRef}
              type="file"
              hidden
              accept={config.accept}
              onChange={(event) => {
                const file = event.target.files?.[0] ?? null;
                applySelectedFile(file);
                event.target.value = "";
              }}
            />
          ) : null}
        </Box>

        <TextField
          label="Título ou descrição (opcional)"
          value={documentDescription}
          onChange={(event) => setDocumentDescription(event.target.value)}
          placeholder="Ex.: Receita de julho ou CNH"
          inputProps={{ maxLength: 200 }}
          helperText={`${documentDescription.length}/200 caracteres`}
          fullWidth
        />

        {/* Observações: oculto por enquanto — ainda não é persistido no FHIR.
            Reativar quando houver campo/annotation no DocumentReference. */}
        {/* <TextField
          label="Observações (opcional)"
          value={notes}
          onChange={(event) => setNotes(event.target.value)}
          multiline
          minRows={2}
          fullWidth
          placeholder="Ex.: validade, número do documento ou orientações da clínica"
        /> */}
      </Stack>
    </CommonDialog>
  );
}
